import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Layout from '../../layouts/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import TimelineMetric from '../../components/ui/TimelineMetric';
import PropertyCard from '../../components/PropertyCard';
import { useFavorites } from '../../hooks/useFavorites';
import { toast } from '../../hooks/use-toast';
import {
  FileText, Calendar, CheckCircle, Clock, Eye, MapPin, Home, Heart
} from 'lucide-react';
import dashboardIcon from '../../assets/img3d/dashboard.png';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const [stats, setStats] = useState({
    total_requests: 0,
    pending_requests: 0,
    scheduled_visits: 0,
    completed_requests: 0
  });
  const [recentRequests, setRecentRequests] = useState([]);
  const [upcomingVisits, setUpcomingVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visitsLoading, setVisitsLoading] = useState(true);
  const { favorites, fetchFavorites, loading: favoritesLoading, addFavorite } = useFavorites();

  useEffect(() => {
    fetchStats();
    fetchRecentRequests();
    fetchUpcomingVisits();
    fetchFavorites();
  }, [fetchFavorites]);

  // Handle automatic favorite addition from query params
  useEffect(() => {
    const propertyIdToAdd = searchParams.get('addFavorite');
    
    if (propertyIdToAdd && isAuthenticated) {
      const addFavoriteAsync = async () => {
        try {
          await addFavorite(propertyIdToAdd);
          toast({
            title: 'Bien ajouté aux favoris',
            description: 'Ce bien a été ajouté à vos coups de cœur !',
          });
          
          // Clean up the query parameter from URL
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('addFavorite');
          setSearchParams(newSearchParams, { replace: true });
          
          // Refresh favorites to show the new one
          await fetchFavorites();
        } catch (error) {
          console.error('Error adding favorite:', error);
          toast({
            title: 'Erreur',
            description: 'Impossible d\'ajouter le bien aux favoris. Vous pouvez le faire manuellement.',
            variant: 'destructive',
          });
          
          // Clean up the query parameter even on error
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('addFavorite');
          setSearchParams(newSearchParams, { replace: true });
        }
      };
      
      addFavoriteAsync();
    }
  }, [searchParams, isAuthenticated, addFavorite, fetchFavorites, setSearchParams]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/client/dashboard/stats`);
      const data = response.data;
      // Support both camelCase (API) and snake_case (legacy/compatibility)
      setStats({
        total_requests: data.totalRequests || data.total_requests || 0,
        pending_requests: data.pendingRequests || data.pending_requests || 0,
        scheduled_visits: data.scheduledVisits || data.scheduled_visits || 0,
        completed_requests: data.completedRequests || data.completed_requests || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats({
        total_requests: 0,
        pending_requests: 0,
        scheduled_visits: 0,
        completed_requests: 0
      });
    }
  };

  const fetchRecentRequests = async () => {
    try {
      const response = await axios.get(`${API}/client/my-requests?limit=3`);
      // L'API retourne { items, total, skip, take }
      const requests = response.data?.items || response.data || [];
      setRecentRequests(Array.isArray(requests) ? requests : []);
    } catch (error) {
      console.error('Error fetching requests:', error);
      setRecentRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcomingVisits = async () => {
    setVisitsLoading(true);
    try {
      const response = await axios.get(`${API}/client/my-visits?upcoming_only=true`);
      const visits = response.data || [];
      // Limiter à 5 visites maximum
      setUpcomingVisits(Array.isArray(visits) ? visits.slice(0, 5) : []);
    } catch (error) {
      console.error('Error fetching visits:', error);
      setUpcomingVisits([]);
    } finally {
      setVisitsLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    // Normaliser le statut en minuscules et gérer les underscores
    const normalizedStatus = (status || '').toLowerCase().replace(/_/g, '_');
    
    const statusConfig = {
      new: { color: 'bg-blue-100 text-blue-800', label: 'Nouveau', icon: Clock },
      qualified: { color: 'bg-purple-100 text-purple-800', label: 'En cours', icon: Clock },
      assigned: { color: 'bg-orange-100 text-orange-800', label: 'Assigné', icon: Clock },
      visit_planned: { color: 'bg-yellow-100 text-yellow-800', label: 'Visite prévue', icon: Calendar },
      visitplanned: { color: 'bg-yellow-100 text-yellow-800', label: 'Visite prévue', icon: Calendar },
      completed: { color: 'bg-green-100 text-green-800', label: 'Terminé', icon: CheckCircle },
      lost: { color: 'bg-red-100 text-red-800', label: 'Clôturé', icon: CheckCircle }
    };

    const config = statusConfig[normalizedStatus] || statusConfig.new;
    const Icon = config.icon;
    return (
      <Badge className={config.color}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateWithTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', { 
      day: 'numeric', 
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Layout
      headerConfig={{
        icon: dashboardIcon,
        iconColor: 'bg-white',
        title: 'Tableau de Bord',
        subtitle: 'Vue d\'ensemble de votre activité',
        primaryAction: 'Rechercher un Bien',
        secondaryAction: undefined,
        tertiaryAction: undefined,
        onPrimaryAction: () => navigate('/properties'),
        onSecondaryAction: undefined,
        onTertiaryAction: undefined
      }}
    >
      {/* Top KPIs - Timeline Metrics */}
      <div className="mb-8 w-full">
        <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden w-full">
          <div className="flex flex-col md:flex-row w-full">
            <div className="flex-1">
              <TimelineMetric
                icon={FileText}
                value={stats.total_requests}
                label="Mes Demandes"
                type="properties"
                onClick={() => navigate('/client/my-requests')}
              />
            </div>
            <div className="flex-1">
              <TimelineMetric
                icon={Clock}
                value={stats.pending_requests}
                label="En Cours"
                type="arrears"
              />
            </div>
            <div className="flex-1">
              <TimelineMetric
                icon={Calendar}
                value={stats.scheduled_visits}
                label="Visites Prévues"
                type="occupancy"
                onClick={stats.scheduled_visits > 0 ? () => navigate('/client/my-visits') : undefined}
              />
            </div>
            <div className="flex-1">
              <TimelineMetric
                icon={CheckCircle}
                value={stats.completed_requests}
                label="Terminées"
                type="owners"
                noBorder={true}
              />
            </div>
          </div>
        </div>
      </div>

        {/* Dashboard Sections - Visites et Demandes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Mes Visites à Venir */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  Mes Visites à Venir
                </h2>
                {upcomingVisits.length > 0 && (
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/client/my-visits')}
                  >
                    Voir toutes
                  </Button>
                )}
              </div>

              {visitsLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF9F68]"></div>
                </div>
              ) : upcomingVisits.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">Aucune visite à venir</p>
              
                </div>
              ) : (
                <div className="space-y-3">
                  {upcomingVisits.map((visit) => {
                    const property = visit.property || {};
                    const visitDate = visit.visit_date || visit.visitDate;
                    const propertyTitle = property.title || property.name || 'Propriété';
                    
                    return (
                      <div
                        key={visit.id}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => navigate(`/client/my-visits`)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Calendar className="h-4 w-4 text-purple-600" />
                              <span className="font-semibold text-gray-900 text-sm">
                                {formatDateWithTime(visitDate)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 line-clamp-1">
                              {propertyTitle}
                            </p>
                            {property.address && (
                              <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                                <MapPin className="h-3 w-3 inline mr-1" />
                                {property.address}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Mes Demandes - Aperçu */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  Mes Demandes
                </h2>
                {recentRequests.length > 0 && (
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/client/my-requests')}
                  >
                    Voir toutes
                  </Button>
                )}
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF9F68]"></div>
                </div>
              ) : recentRequests.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm mb-2">Aucune demande pour le moment</p>
            
                </div>
              ) : (
                <div className="space-y-3">
                  {recentRequests.map((request) => {
                    const propertyTitle =
                      request.property?.title ||
                      request.propertyTitle ||
                      request.property_title ||
                      null;

                    return (
                      <div
                        key={request.id}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => navigate(`/client/my-requests/${request.id}`)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-gray-900 text-sm">
                                {propertyTitle
                                  ? `Demande pour « ${propertyTitle} »`
                                  : `Demande ${request.id ? `#${request.id.slice(0, 8)}` : ''}`}
                              </h3>
                              {getStatusBadge(request.status)}
                            </div>
                            <p className="text-xs text-gray-500">
                              <Clock className="h-3 w-3 inline mr-1" />
                              {formatDate(request.createdAt || request.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Mes Coups de Cœur */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                Mes Coups de Cœur
              </h2>
              {favorites.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/client/favorites')}
                >
                  Voir toutes
                </Button>
              )}
            </div>

            {favoritesLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF9F68]"></div>
              </div>
            ) : favorites.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Heart className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm mb-2">Aucun coup de cœur pour le moment</p>
                <p className="text-xs text-gray-400 mt-1">
                  Sauvegardez vos biens favoris pour y revenir facilement
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {favorites.map((favorite) => (
                  <PropertyCard
                    key={favorite.id}
                    property={favorite.property}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
    </Layout>
  );
};

export default ClientDashboard;
